'use client';
import { LegendIcon } from '@mnd-frontend/charts';
import { Text } from '@mnd-frontend/ui/publish/components';
import { GREY_800 } from '@mnd-frontend/ui/shared/tokens';
import { localizeValue } from '@mnd-frontend/utils';
import styled from 'styled-components';
import { useInteraction } from '../../../contexts/InteractionContext';
import { useReportTranslation } from '../../../hooks';
import type { TranslationFunction } from '../../../types';
import { TrendPill } from '../../common';
import { ScrollContainer } from '../../common/ScrollContainer';

const FirstColumn = styled.div`
  text-align: left;
  max-width: calc(40% - 0.5rem);
  flex: 0.4 0 0;
  margin-right: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NameColumn = styled(FirstColumn)<{ $highlighted?: boolean }>`
  display: flex;
  align-items: center;
  > *:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    ${props => props.$highlighted && 'font-weight: 700;'}
  }
`;

const DataColumn = styled.div`
  flex: 0.2 0 0;
  max-width: calc(20% - 0.5rem);
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const formatValue = (value: number, type: 'number' | 'percentage' | 'trend') => {
  switch (type) {
    case 'number':
      return localizeValue(value, { abbrev: true });
    case 'percentage':
      return localizeValue(value, { percentage: true, precision: 1, minPrecision: 0 });
    case 'trend':
      return value === 0
        ? '-'
        : (value > 0 ? '+' : '') + localizeValue(value, { percentage: true, precision: 0 });
    default:
      return value;
  }
};

const FlexRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const LegendItemWrapper = styled(FlexRow)`
  color: ${GREY_800};
  cursor: pointer;
  > * {
    pointer-events: none;
  }
`;

const SentimentLegendItem = ({
  id,
  value,
  name,
  color,
  type,
}: {
  id: string;
  value: number;
  name: string;
  color: string;
  type: 'percentage' | 'number';
}) => {
  const { setHovered, hovered, onClick } = useInteraction();
  return (
    <LegendItemWrapper
      onClick={() => onClick(id)}
      onMouseOver={() => hovered !== id && setHovered(id)}
      onMouseOut={() => hovered === id && setHovered(null)}
    >
      <NameColumn>
        <LegendIcon color={color} />
        <Text as="span" color="GREY_800" variant="body">
          {name}
        </Text>
      </NameColumn>
      <DataColumn>{formatValue(value, type)}</DataColumn>
    </LegendItemWrapper>
  );
};
const LegendContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
  justify-content: center;
`;
export const LegendSentimentOverview = ({
  data,
}: {
  data: {
    value: number;
    label: string;
    color: string;
    id: string;
  }[];
}) => {
  return (
    <LegendContainer>
      {data.map(({ value, label, color, id }) => (
        <SentimentLegendItem
          key={id}
          id={id}
          value={value}
          name={label}
          color={color}
          type="percentage"
        />
      ))}
    </LegendContainer>
  );
};

type LegendItemShareOfVoiceData = {
  color: string;
  highlighted: boolean;
  id: string;
  label: string;
  name: string;
  share: number;
  trend: number;
  value: number;
};
interface LegendTableProps {
  data: LegendItemShareOfVoiceData[];
}
const getDataColumns = ({ t }: { t: TranslationFunction }) => {
  return [
    {
      dataKey: 'value',
      type: 'number',
      headingKey: t('common_mentions'),
    },
    {
      dataKey: 'share',
      type: 'percentage',
      headingKey: t('reports_share_of_voice_short'),
    },
    {
      dataKey: 'trend',
      type: 'trend',
      headingKey: t('reports_trend'),
    },
  ] as const;
};

const LegendItemShareOfVoice = ({ item }: { item: LegendItemShareOfVoiceData }) => {
  const { t } = useReportTranslation();
  const { setHovered, hovered, onClick } = useInteraction();

  return (
    <LegendItemWrapper
      onClick={() => onClick(item.id)}
      onMouseOver={() => hovered !== item.id && setHovered(item.id)}
      onMouseOut={() => hovered === item.id && setHovered(null)}
    >
      <NameColumn $highlighted={item.highlighted}>
        <LegendIcon color={item.color} />
        <Text as="span" color="GREY_800" variant="body">
          {item.name}
        </Text>
      </NameColumn>
      {getDataColumns({ t }).map(({ dataKey, type }) => (
        <DataColumn key={dataKey}>
          {dataKey === 'trend' ? (
            item[dataKey] === 0 ? (
              '-'
            ) : (
              <TrendPill
                direction={item[dataKey] > 0 ? 'positive' : 'negative'}
                value={Math.round(item[dataKey]) + '%'}
              />
            )
          ) : (
            formatValue(item[dataKey], type)
          )}
        </DataColumn>
      ))}
    </LegendItemWrapper>
  );
};

export const LegendTableShareOfVoice = ({ data }: LegendTableProps) => {
  const { t } = useReportTranslation();
  const highlighted = data.filter(item => item.highlighted);
  const notHighlighted = data.filter(item => !item.highlighted);
  const dataColumns = getDataColumns({ t });

  return (
    <ScrollContainer minWidth="25rem">
      <LegendContainer>
        <FlexRow>
          <FirstColumn>
            <Text as="span" variant="reportLabelSmall">
              {t('common_brand')}
            </Text>
          </FirstColumn>
          {dataColumns.map(d => (
            <DataColumn key={d.dataKey}>
              <Text as="span" variant="reportLabelSmall">
                {t(d.headingKey)}
              </Text>
            </DataColumn>
          ))}
        </FlexRow>
        {highlighted.map(item => (
          <LegendItemShareOfVoice key={item.id} item={item} />
        ))}
        {notHighlighted.map(item => (
          <LegendItemShareOfVoice key={item.id} item={item} />
        ))}
      </LegendContainer>
    </ScrollContainer>
  );
};
